import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronRight, Clock } from "lucide-react";
import { Navbar } from "@/components/landing/Navbar";
import { Footer } from "@/components/landing/Footer";
import { Icon } from "@/components/landing/Icon";
import { Reveal, staggerContainer, staggerItem } from "@/components/landing/Reveal";
import { AmbientOrbs } from "@/components/landing/Ambient";
import { ScrollProgress, WhatsAppFab } from "@/components/landing/Floating";
import { api } from "@/lib/api";
import { CATEGORY_CONFIG } from "@/lib/categories";
import SEO from "@/components/SEO";

export default function CategoryHub({ categorySlug }) {
  const config = CATEGORY_CONFIG[categorySlug];
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!config) return;
    setLoading(true);
    api
      .listServices(config.dbCategory)
      .then(({ services }) => setServices(services || []))
      .catch(() => setServices([]))
      .finally(() => setLoading(false));
  }, [config, categorySlug]);

  if (!config) return <Navigate to="/services" replace />;

  const hubMeta = {
    "business-licences": {
      title: "Business Licences & Statutory Registrations in India | Biz360 Compliance",
      desc: "Acquire key operating licences: Udyam MSME registration, Maharashtra Shop & Establishment (Gumasta), FSSAI food licence, Import Export Code (IEC) & Digital Signatures.",
      keywords: "Udyam registration India, Gumasta licence Pune, Shop act Maharashtra, FSSAI registration, IEC code, MSME certificate",
    },
    "accounting-finance": {
      title: "Accounting, Bookkeeping & Virtual CFO Services | Biz360 Compliance",
      desc: "Professional accounting, bookkeeping, monthly MIS reporting, Virtual CFO, cash flow planning, and payroll management for businesses in Maharashtra & India.",
      keywords: "accounting services Pune, bookkeeping India, virtual CFO Maharashtra, MIS reports, payroll management",
    },
    "other-services": {
      title: "Business Advisory, Audit Support & Growth Services | Biz360 Compliance",
      desc: "Specialised business advisory: Startup India DPIIT recognition, project reports for bank financing, internal audits, and MSME growth solutions.",
      keywords: "Startup India registration, project report Pune, MSME advisory Maharashtra, internal audit support",
    },
  }[categorySlug] || {
    title: `${config.label} | Biz360 Compliance`,
    desc: config.tagline,
    keywords: "business compliance India, professional advisory Pune",
  };

  const breadcrumbItems = config.parent
    ? [config.parent, { label: config.label, href: `/${categorySlug}` }]
    : [{ label: config.label, href: `/${categorySlug}` }];

  return (
    <div className="relative min-h-screen bg-white font-sans text-navy-900 antialiased" data-testid="category-hub-page">
      <SEO
        title={hubMeta.title}
        description={hubMeta.desc}
        canonical={`/${categorySlug}`}
        keywords={hubMeta.keywords}
        breadcrumbs={breadcrumbItems}
      />
      <ScrollProgress />
      <Navbar />
      <main>
        <section className="relative overflow-hidden bg-[rgb(238,242,250)] pt-32 pb-16 md:pt-40 md:pb-20">
          <AmbientOrbs />
          <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-6 flex flex-wrap items-center gap-1.5 text-xs font-medium text-slate-500" data-testid="category-hub-breadcrumb">
                <Link to="/" className="hover:text-navy-800">Home</Link>
                <ChevronRight className="h-3 w-3" />
                {config.parent && (
                  <>
                    <Link to={config.parent.href} className="hover:text-navy-800">{config.parent.label}</Link>
                    <ChevronRight className="h-3 w-3" />
                  </>
                )}
                <span className="text-navy-700">{config.label}</span>
              </div>
            </Reveal>
            <div className="mx-auto max-w-2xl text-center">
              <Reveal>
                <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
                  {config.label}
                </span>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-navy-900 md:text-5xl">{config.label}</h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-4 text-base text-slate-600 md:text-lg">{config.tagline}</p>
              </Reveal>
            </div>
          </div>
        </section>

        <section className="relative bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            {loading ? (
              <p className="py-16 text-center text-slate-500">Loading services...</p>
            ) : (
              <motion.div
                variants={staggerContainer}
                initial="hidden"
                whileInView="show"
                viewport={{ once: false, margin: "-60px" }}
                className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
              >
                {services.map((s, i) => (
                  <motion.div key={s.slug} variants={staggerItem}>
                    <Link
                      to={`/${categorySlug}/${s.slug}`}
                      data-testid={`category-card-${s.slug}`}
                      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)]"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img src={s.image} alt={s.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                        <span className={`absolute left-4 top-4 rounded-full px-3 py-1 text-xs font-semibold backdrop-blur ${i % 2 === 0 ? "bg-navy-900/90 text-gold-400" : "bg-emerald2-600/95 text-white"}`}>
                          {config.label}
                        </span>
                        <span className="absolute right-4 top-4 grid h-9 w-9 place-items-center rounded-xl bg-white/95 text-navy-900 shadow-[0_6px_16px_rgba(15,30,60,0.18)]">
                          <Icon name={s.icon} className="h-4 w-4" />
                        </span>
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        <h3 className="text-lg font-semibold leading-snug text-navy-900 transition-colors group-hover:text-gold-600">{s.title}</h3>
                        <p className="mt-2.5 line-clamp-2 text-sm leading-relaxed text-slate-500">{s.subtitle}</p>
                        <div className="mt-auto flex items-center justify-between pt-5">
                          <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
                            <Clock className="h-3.5 w-3.5" /> {s.read_time}
                          </div>
                          <span className="flex items-center gap-1 text-xs font-semibold text-emerald2-600">
                            View details <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFab />
    </div>
  );
}
